import type { TFunction } from 'i18next';
import { useMemo } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { SvgIcon, styled } from '@mui/material';

import { invertPattern, minLength, noSpaceAround, pattern } from 'common/utils/validations';
import { ValidIndicatorLabel } from 'common/viewcomp/forms/fields/indicator';

interface Props {
    password: string;
    t: TFunction;
}

export const PasswordValidIndicators = ({ password, t }: Props) => {
    const isAllValid = useMemo(() => {
        return !!(
            password &&
            minLength(8, null)(password) === undefined &&
            invertPattern('[^a-zA-Z0-9@.\\-_+$%!?\\s]', null)(password) === undefined &&
            noSpaceAround(null)(password) === undefined &&
            pattern('[0-9]', null)(password) === undefined &&
            pattern('[a-zA-Z]', null)(password) === undefined &&
            pattern('[$%@!?\\-+]', null)(password) === undefined
        );
    }, [password]);

    if (isAllValid) {
        return null;
    }

    const validIcon = <SvgIcon component={ValidIcon} />;
    const invalidIcon = <SvgIcon component={InvalidIcon} />;

    return (
        <div>
            <ValidIndicatorLabel
                value={password}
                validator={minLength(8, null)}
                label={t('password_form.too_short')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />
            <ValidIndicatorLabel
                value={password}
                validator={pattern('[a-zA-Z]', null)}
                label={t('password_form.one_letter')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />
            <ValidIndicatorLabel
                value={password}
                validator={pattern('[0-9]', null)}
                label={t('password_form.need_number')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />
            <ValidIndicatorLabel
                value={password}
                validator={pattern('[$%@!?\\-+]', null)}
                label={t('password_form.need_special_characters')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />

            <ValidIndicatorLabel
                value={password}
                validator={invertPattern('[^a-zA-Z0-9@.\\-_+$%!?\\s]', null)}
                label={t('password_form.contain_invalid_characters')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />

            <ValidIndicatorLabel
                value={password}
                validator={noSpaceAround(null)}
                label={t('password_form.trim_space')}
                validIcon={validIcon}
                invalidIcon={invalidIcon}
            />
        </div>
    );
};

const ValidIcon = styled(CheckCircleIcon)(({ theme }) => ({
    color: theme.ciPalette.brand.green,
}));

const InvalidIcon = styled(ErrorIcon)(({ theme }) => ({
    color: theme.ciPalette.brand.red,
}));
